import {
  DEFAULT_JOB_VIEW,
  JOB_COLUMN_LABELS,
  JOB_COLUMNS,
  JOB_SORT_LABELS,
  JOB_SORTS,
  type JobColumn,
  type JobFilters,
  type JobListItem,
  type JobSort,
  type JobViewItem,
  type JobViewState,
  type JobViewsResponse,
  MONEY_COLUMNS,
  type RoleItem,
  jobViewFromParams,
  jobViewToParams,
  sameJobView,
} from "@nmt/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { del, errorMessage, get, patch, post, put } from "../api";
import { NewJobDialog } from "../components/forms";
import { Alert, Button, Checkbox, Dialog, Empty, Field, Loading, PageHeader, Panel, SelectField, StageBadge, ago, cx, formatCents } from "../components/ui";
import { useUsers, useWorkflows } from "../lookups";
import { useSession } from "../session";

type Page = { items: JobListItem[]; more: boolean };
const PAGE = 50;
const LAST_VIEW_KEY = "jobs:view";
const LAST_LIST_KEY = "jobs:list";
const store = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private browsing: not remembered.
  }
};
const stored = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const remember = (id: string) => store(LAST_VIEW_KEY, id);

/** The Jobs list: filters, sort, and columns kept in the address, and saved views to switch between. */
export function JobsPage() {
  const { can } = useSession();
  const [adding, setAdding] = useState(false);
  const [params, setParams] = useSearchParams();
  const views = useQuery({ queryKey: ["job-views"], queryFn: () => get<JobViewsResponse>("/api/job-views") });
  const state = jobViewFromParams(params);
  const viewId = params.get("view");
  const active = views.data?.views.find((v) => v.id === viewId) ?? null;
  const changed = active ? !sameJobView(state, active) : !sameJobView(state, DEFAULT_JOB_VIEW);

  const go = (next: JobViewState, id: string | null) => {
    const p = jobViewToParams(next);
    if (id) p.set("view", id);
    setParams(p, { replace: true });
  };
  const open = (v: JobViewItem | null) => {
    remember(v?.id ?? "default");
    go(v ?? DEFAULT_JOB_VIEW, v?.id ?? null);
  };
  const setState = (next: JobViewState) => go(next, viewId);

  // Landing on a bare /jobs: the list as it was left on this device (filters, sort, columns),
  // else the last view used here, else the one set to open for me.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !views.data) return;
    opened.current = true;
    if (params.toString()) return;
    const list = stored(LAST_LIST_KEY);
    if (list !== null) {
      if (list) setParams(new URLSearchParams(list), { replace: true });
      return;
    }
    const last = stored(LAST_VIEW_KEY);
    const pick = last === "default" ? null : (views.data.views.find((v) => v.id === last) ?? views.data.views.find((v) => v.id === views.data!.defaultViewId) ?? null);
    if (pick) go(pick, pick.id);
  }, [views.data]);

  // Keep the list as it is now, once the landing above has had its turn.
  useEffect(() => {
    if (opened.current) store(LAST_LIST_KEY, params.toString());
  }, [params, views.data]);

  return (
    <div>
      <PageHeader
        title="Jobs"
        description="Every job belongs to a customer. New job lets you pick one or add a new customer with it."
        action={
          can("jobs.edit") && (
            <Button variant="primary" className="px-3 py-1.5" onClick={() => setAdding(true)}>
              + New job
            </Button>
          )
        }
      />
      {adding && <NewJobDialog onClose={() => setAdding(false)} />}
      <ViewBar views={views.data} active={active} changed={changed} state={state} onOpen={open} />
      <FilterBar state={state} onChange={setState} />
      <JobTable state={state} />
    </div>
  );
}

/** Default view, the saved views, and saving the current list. */
function ViewBar({ views, active, changed, state, onOpen }: { views: JobViewsResponse | undefined; active: JobViewItem | null; changed: boolean; state: JobViewState; onOpen: (v: JobViewItem | null) => void }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState<"new" | "edit" | null>(null);
  const update = useMutation({
    mutationFn: () => patch<JobViewItem>(`/api/job-views/${active!.id}`, state),
    onSuccess: (v) => (void qc.invalidateQueries({ queryKey: ["job-views"] }), onOpen(v)),
  });
  const setDefault = useMutation({ mutationFn: (id: string | null) => put("/api/me/job-view", { viewId: id }), onSuccess: () => qc.invalidateQueries({ queryKey: ["job-views"] }) });
  const list = views?.views ?? [];
  const isDefault = (id: string | null) => (views?.defaultViewId ?? null) === id;
  const chip = (label: string, on: boolean, onClick: () => void, key: string) => (
    <button key={key} onClick={onClick} className={cx("shrink-0 rounded-full px-3 py-1 text-sm font-medium", on ? "bg-brand text-white" : "bg-mist text-ink-soft hover:text-ink")}>
      {label}
    </button>
  );
  return (
    <div className="mb-3 flex flex-col gap-2">
      <div className="hidden flex-wrap items-center gap-1.5 sm:flex">
        {chip("Default view", !active, () => onOpen(null), "default")}
        {list.map((v) => chip(v.name, active?.id === v.id, () => onOpen(v), v.id))}
      </div>
      <div className="sm:hidden">
        <SelectField label="View" value={active?.id ?? ""} onChange={(e) => onOpen(list.find((v) => v.id === e.target.value) ?? null)}>
          <option value="">Default view</option>
          {list.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </SelectField>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {changed && <span className="text-muted">{active ? `Changed from "${active.name}"` : "Changed from the default view"}</span>}
        {changed && active?.canEdit && (
          <Button variant="quiet" className="px-2 py-1" busy={update.isPending} onClick={() => update.mutate()}>
            Save changes
          </Button>
        )}
        {changed && (
          <Button variant="quiet" className="px-2 py-1" onClick={() => setSaving("new")}>
            Save as a view…
          </Button>
        )}
        {active?.canEdit && (
          <Button variant="quiet" className="px-2 py-1" onClick={() => setSaving("edit")}>
            Edit view…
          </Button>
        )}
        {!isDefault(active?.id ?? null) ? (
          <Button variant="quiet" className="px-2 py-1" busy={setDefault.isPending} onClick={() => setDefault.mutate(active?.id ?? null)}>
            Open this view for me
          </Button>
        ) : (
          views && <span className="text-xs text-muted">Opens for you</span>
        )}
        {(update.error || setDefault.error) && <span className="text-danger">{errorMessage(update.error ?? setDefault.error)}</span>}
      </div>
      {saving && <SaveViewDialog view={saving === "edit" ? active : null} state={state} onClose={() => setSaving(null)} onSaved={(v) => (setSaving(null), onOpen(v))} />}
    </div>
  );
}

/** Saves the current list as a new view, or renames, shares, or deletes one. */
function SaveViewDialog({ view, state, onClose, onSaved }: { view: JobViewItem | null; state: JobViewState; onClose: () => void; onSaved: (v: JobViewItem | null) => void }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles"), enabled: can("settings.manage") });
  const [name, setName] = useState(view?.name ?? "");
  const [shared, setShared] = useState(view?.shared ?? false);
  const [roleIds, setRoleIds] = useState<string[]>(view?.roleIds ?? []);
  const save = useMutation({
    mutationFn: () => (view ? patch<JobViewItem>(`/api/job-views/${view.id}`, { name: name.trim(), shared, roleIds }) : post<JobViewItem>("/api/job-views", { ...state, name: name.trim(), shared, roleIds })),
    onSuccess: (v) => (void qc.invalidateQueries({ queryKey: ["job-views"] }), onSaved(v)),
  });
  const remove = useMutation({ mutationFn: () => del(`/api/job-views/${view!.id}`), onSuccess: () => (void qc.invalidateQueries({ queryKey: ["job-views"] }), onSaved(null)) });
  return (
    <Dialog
      title={view ? `Edit "${view.name}"` : "Save as a view"}
      onClose={onClose}
      footer={
        <>
          {view && (
            <Button variant="danger" className="mr-auto" busy={remove.isPending} onClick={() => window.confirm(`Delete the view "${view.name}"?`) && remove.mutate()}>
              Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {(save.error || remove.error) && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <Field label="Name" placeholder="Insurance jobs in Moore" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} autoFocus />
        {!view && <p className="text-muted">It keeps the filters, sort, and columns on screen now.</p>}
        {can("settings.manage") ? (
          <>
            <Checkbox label="Share it" description="Others see it in their views. Leave all roles unticked for everyone." checked={shared} onChange={setShared} />
            {shared && (
              <div className="grid grid-cols-2 pl-6">
                {roles.data?.map((r) => (
                  <Checkbox key={r.id} label={r.name} checked={roleIds.includes(r.id)} onChange={(on) => setRoleIds(on ? [...roleIds, r.id] : roleIds.filter((x) => x !== r.id))} />
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-muted">Just for you. (Sharing views with others needs permission to manage settings.)</p>
        )}
      </div>
    </Dialog>
  );
}

/** A button that opens a list of checkboxes (stages, lead sources, columns). */
function Picker({ label, summary, children }: { label: string; summary: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <div className="mb-1 text-sm font-semibold text-ink-soft">{label}</div>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-left text-base sm:text-sm">
        <span className="truncate">{summary}</span>
        <span aria-hidden className="ml-2 text-muted">
          ▾
        </span>
      </button>
      {open && <div className="absolute z-20 mt-1 max-h-80 w-full min-w-56 overflow-y-auto rounded-[var(--radius-control)] border border-line bg-white p-1 shadow-lg">{children}</div>}
    </div>
  );
}

function FilterBar({ state, onChange }: { state: JobViewState; onChange: (s: JobViewState) => void }) {
  const { me, can } = useSession();
  const workflows = useWorkflows();
  const users = useUsers();
  const f = state.filters;
  const [q, setQ] = useState(f.q);
  const [more, setMore] = useState(false);
  const set = (p: Partial<JobFilters>) => onChange({ ...state, filters: { ...f, ...p } });
  useEffect(() => setQ(f.q), [f.q]);
  useEffect(() => {
    const t = window.setTimeout(() => q !== f.q && set({ q }), 300);
    return () => window.clearTimeout(t);
  }, [q]);
  const flows = workflows.data ?? [];
  const stageFlows = f.workflowId ? flows.filter((w) => w.id === f.workflowId) : flows;
  const stageName = (id: string) => flows.flatMap((w) => w.stages).find((s) => s.id === id)?.name ?? "stage";
  const sources = me?.lists.leadSources ?? [];
  const columns = JOB_COLUMNS.filter((c) => can("money.view") || !MONEY_COLUMNS.includes(c));
  const sorts = JOB_SORTS.filter((s) => s !== "contract" || can("money.view"));
  const extra = [f.leadSources.length, f.place, f.stageDaysMin, f.createdFrom || f.createdTo, f.updatedFrom || f.updatedTo].filter(Boolean).length;
  const toggle = <T,>(list: T[], v: T, on: boolean) => (on ? [...list, v] : list.filter((x) => x !== v));
  // Phones: just the search and a Filters button; the rest opens from it.
  const [phoneOpen, setPhoneOpen] = useState(false);
  const on = [f.workflowId, f.stageIds.length || f.firstStage, f.repId, f.status !== "open", state.sort !== "updated"].filter(Boolean).length + extra;
  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex items-end gap-2 sm:hidden">
        <input
          type="search"
          aria-label="Search jobs"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name, address, #, claim"
          className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-base"
        />
        <Button variant={on ? "primary" : "secondary"} className="shrink-0 px-3 py-2" onClick={() => setPhoneOpen(!phoneOpen)} aria-expanded={phoneOpen}>
          {phoneOpen ? "Done" : `Filters${on ? ` (${on})` : ""}`}
        </Button>
      </div>
      <div className={cx("grid gap-3 sm:grid-cols-2 lg:grid-cols-6", !phoneOpen && "hidden sm:grid")}>
        <div className="hidden sm:block">
          <label htmlFor="job-q" className="mb-1 block text-sm font-semibold text-ink-soft">
            Search
          </label>
          <input
            id="job-q"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, address, #, claim"
            className="w-full rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none sm:text-sm"
          />
        </div>
        <SelectField label="Job type" value={f.workflowId ?? ""} onChange={(e) => set({ workflowId: e.target.value || null, stageIds: [] })}>
          <option value="">All types</option>
          {flows.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </SelectField>
        <Picker label="Stage" summary={f.firstStage ? "New leads (first stage)" : !f.stageIds.length ? "All stages" : f.stageIds.length === 1 ? stageName(f.stageIds[0]!) : `${f.stageIds.length} stages`}>
          <Checkbox label="New leads (each type's first stage)" checked={f.firstStage} onChange={(on) => set({ firstStage: on, stageIds: on ? [] : f.stageIds })} />
          {stageFlows.map((w) => (
            <div key={w.id}>
              {stageFlows.length > 1 && <div className="px-2 pt-2 text-xs font-semibold text-muted uppercase">{w.name}</div>}
              {w.stages.map((s) => (
                <Checkbox key={s.id} label={s.name} checked={f.stageIds.includes(s.id)} onChange={(on) => set({ stageIds: toggle(f.stageIds, s.id, on), firstStage: false })} />
              ))}
            </div>
          ))}
        </Picker>
        <SelectField label="Rep" value={f.repId ?? ""} onChange={(e) => set({ repId: (e.target.value || null) as JobFilters["repId"] })}>
          <option value="">Anyone</option>
          <option value="me">Me</option>
          <option value="none">Nobody assigned</option>
          {users.data?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </SelectField>
        <SelectField label="Status" value={f.status} onChange={(e) => set({ status: e.target.value as JobFilters["status"] })}>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="lost">Lost</option>
          <option value="all">All</option>
        </SelectField>
        <SelectField label="Sort by" value={state.sort} onChange={(e) => onChange({ ...state, sort: e.target.value as JobSort })}>
          {sorts.map((s) => (
            <option key={s} value={s}>
              {JOB_SORT_LABELS[s]}
            </option>
          ))}
        </SelectField>
      </div>
      <div className={cx("flex-wrap items-center gap-3 text-sm", phoneOpen ? "flex" : "hidden sm:flex")}>
        <Button variant="quiet" className="px-2 py-1" onClick={() => setMore(!more)}>
          {more ? "Fewer filters" : `More filters${extra ? ` (${extra} on)` : ""}`}
        </Button>
        <div className="w-48">
          <Picker label="" summary={`Columns (${state.columns.length})`}>
            {columns.map((c) => (
              <Checkbox key={c} label={JOB_COLUMN_LABELS[c]} checked={state.columns.includes(c)} onChange={(on) => onChange({ ...state, columns: JOB_COLUMNS.filter((x) => (x === c ? on : state.columns.includes(x))) })} />
            ))}
          </Picker>
        </div>
      </div>
      {more && (
        <div className={cx("grid gap-3 rounded-[var(--radius-panel)] bg-mist/50 p-3 sm:grid-cols-2 lg:grid-cols-4", !phoneOpen && "hidden sm:grid")}>
          <Picker label="Lead source" summary={!f.leadSources.length ? "Any" : f.leadSources.length === 1 ? f.leadSources[0]! : `${f.leadSources.length} sources`}>
            {sources.length ? sources.map((s) => <Checkbox key={s} label={s} checked={f.leadSources.includes(s)} onChange={(on) => set({ leadSources: toggle(f.leadSources, s, on) })} />) : <p className="px-2 py-1 text-muted">No lead sources set up (Settings → Lists).</p>}
          </Picker>
          <Field label="City or ZIP" value={f.place} placeholder="Moore or 73160" onChange={(e) => set({ place: e.target.value })} />
          <Field label="In the same stage at least (days)" inputMode="numeric" value={f.stageDaysMin ? String(f.stageDaysMin) : ""} onChange={(e) => set({ stageDaysMin: Math.min(3650, Math.max(0, Math.floor(Number(e.target.value) || 0))) || null })} />
          <div />
          <Field label="Created from" type="date" value={f.createdFrom ?? ""} onChange={(e) => set({ createdFrom: e.target.value || null })} />
          <Field label="Created to" type="date" value={f.createdTo ?? ""} onChange={(e) => set({ createdTo: e.target.value || null })} />
          <Field label="Updated from" type="date" value={f.updatedFrom ?? ""} onChange={(e) => set({ updatedFrom: e.target.value || null })} />
          <Field label="Updated to" type="date" value={f.updatedTo ?? ""} onChange={(e) => set({ updatedTo: e.target.value || null })} />
        </div>
      )}
    </div>
  );
}

const days = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));

function cell(c: JobColumn, j: JobListItem): ReactNode {
  switch (c) {
    case "address":
      return <span className="text-sm text-ink-soft">{[j.street, j.city].filter(Boolean).join(", ")}</span>;
    case "stage":
      return <StageBadge name={j.stageName} kind={j.stageKind} />;
    case "rep":
      return j.salesRep ? (
        <span className="inline-flex items-center gap-1.5 text-sm whitespace-nowrap">
          <span className="size-2.5 rounded-full" style={{ background: j.salesRep.color }} aria-hidden />
          {j.salesRep.name}
        </span>
      ) : null;
    case "updated":
      return <span className="text-sm text-muted">{ago(j.updatedAt)}</span>;
    case "created":
      return <span className="text-sm text-muted">{ago(j.createdAt)}</span>;
    case "leadSource":
      return <span className="text-sm">{j.leadSource}</span>;
    case "stageDays":
      return <span className="text-sm">{days(j.stageChangedAt)}</span>;
    case "contract":
      return <span className="text-sm whitespace-nowrap">{j.contractCents ? formatCents(j.contractCents) : ""}</span>;
    case "balance":
      return <span className="text-sm whitespace-nowrap">{j.balanceCents ? formatCents(j.balanceCents) : ""}</span>;
    case "claim":
      return <span className="text-sm">{j.claimNumber}</span>;
    case "carrier":
      return <span className="text-sm">{j.insuranceCarrier}</span>;
  }
}

function JobTable({ state }: { state: JobViewState }) {
  const { can } = useSession();
  const qs = jobViewToParams(state);
  const list = useInfiniteQuery({
    queryKey: ["jobs", qs.toString()],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(qs);
      p.set("limit", String(PAGE));
      p.set("offset", String(pageParam));
      return get<Page>(`/api/jobs?${p}`);
    },
    getNextPageParam: (last, pages) => (last.more ? pages.length * PAGE : undefined),
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const cols = state.columns.filter((c) => can("money.view") || !MONEY_COLUMNS.includes(c));
  const right = (c: JobColumn) => c === "contract" || c === "balance" || c === "stageDays";
  if (list.isLoading) return <Loading />;
  if (list.error) return <Alert>{errorMessage(list.error)}</Alert>;
  if (!items.length)
    return (
      <Panel>
        <Empty>No jobs match.</Empty>
      </Panel>
    );
  return (
    <>
      <Panel className="overflow-x-auto">
        <table className="table-base">
          <thead>
            <tr>
              <th>Job</th>
              {cols.map((c) => (
                <th key={c} className={cx(right(c) && "text-right")}>
                  {JOB_COLUMN_LABELS[c]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((j) => (
              <tr key={j.id} className="hover:bg-mist/50">
                <td>
                  <Link to={`/jobs/${j.id}`} className="font-semibold hover:text-brand">
                    #{j.number} {j.customerName}
                  </Link>
                  <div className="text-sm text-muted">{[j.workflowName, j.title].filter(Boolean).join(" · ")}</div>
                </td>
                {cols.map((c) => (
                  <td key={c} className={cx(right(c) && "text-right")}>
                    {cell(c, j)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      {list.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button onClick={() => list.fetchNextPage()} busy={list.isFetchingNextPage}>
            Show more
          </Button>
        </div>
      )}
    </>
  );
}
